import i18next from 'i18next';
import { isEmpty } from 'lodash';

import {
    CHART_COLORS,
    DAYS_IN_WEEK,
    DAYS_NAMES,
    ENERGY_CHART_COLORS,
    HOURS_IN_DAY,
    HOURS_IN_YEAR,
    MONTHS,
} from 'common/constants';
import {
    differenceInDaysDate,
    formatDate,
    parseDate,
} from 'common/utils/dates';
import {
    downloadFile,
    getDayLabel,
    getMonthLabel,
    numberFormat,
} from 'common/utils/helpers';

export const MONTHS_ARRAY = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export const COLOR_DELTA = 25;

// Utils
export const createFilledArray = (length, value = 0) =>
    new Array(length).fill(value);

export const generateColorTone = (baseColor, delta) => {
    let r = parseInt(baseColor.slice(1, 3), 16);
    let g = parseInt(baseColor.slice(3, 5), 16);
    let b = parseInt(baseColor.slice(5, 7), 16);

    r = Math.min(255, Math.max(0, r + delta));
    g = Math.min(255, Math.max(0, g + delta));
    b = Math.min(255, Math.max(0, b + delta));

    const newColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
        .toString(16)
        .slice(1)
        .toUpperCase()}`;

    return newColor;
};

export const getDaysByWeek = (weeks) =>
    weeks.reduce((acc, curr) => {
        for (let i = DAYS_IN_WEEK * curr; i < DAYS_IN_WEEK * (curr + 1); i++)
            if (!acc.includes(i + 1)) acc.push(i + 1);
        return acc;
    }, []);

export const getProposalChartInfoPriceSeries = (
    consumptionHistory,
    keyNames,
) => {
    if (!consumptionHistory || !keyNames) return [];
    const temp = {};
    const allKeys = new Set();
    let correctKeyNames = keyNames;

    if (typeof correctKeyNames === 'string') correctKeyNames = [keyNames];

    // Collect all unique keys
    for (let i = 0; i < consumptionHistory.length; i++) {
        const monthData = consumptionHistory[i];
        const keyName = correctKeyNames[i] || correctKeyNames[0];
        if (!monthData[keyName]) continue;
        const currentKeys = Object.keys(monthData[keyName]);
        for (const key of currentKeys) allKeys.add(key);
    }

    // Fill in the values for each key, or 0 if the key does not exist in the current monthData
    for (let i = 0; i < consumptionHistory.length; i++) {
        const monthData = consumptionHistory[i];
        const keyName = correctKeyNames[i] || correctKeyNames[0];

        for (const key of allKeys) {
            if (!Object.prototype.hasOwnProperty.call(temp, key))
                temp[key] = [];
            temp[key].push(monthData[keyName]?.[key] || 0);
        }
    }

    // Convert temp to the desired series format
    return Array.from(allKeys).map((key) => ({ data: temp[key], name: key }));
};

// Charts config
export const getEconomicChartTooltipMessage = ({
    consumptionHistory,
    countryCurrencyIso,
    countryCurrencyLocale,
    dataPointIndex,
    value,
    w,
}) => {
    let prelabel = value;
    if (consumptionHistory)
        prelabel = getTooltipDateRange({
            index: dataPointIndex,
            seriesEnergy: consumptionHistory,
        });

    return `${prelabel || ''} - <strong>${i18next.t('Total')}: ${numberFormat(
        w.globals.stackedSeriesTotals[dataPointIndex],
        {
            currency: countryCurrencyIso,
            locale: countryCurrencyLocale,
            style: 'currency',
        },
    )}
        </strong>`;
};

export const getTooltipDateRange = ({ index, seriesEnergy }) => {
    if (!seriesEnergy || !seriesEnergy[index]) return '';

    const { final_date, initial_date, label } = seriesEnergy[index];
    const parsedInitialDate = parseDate(initial_date, 'dd/MM/yyyy');
    const parsedFinalDate = parseDate(final_date, 'dd/MM/yyyy');

    const initialDate = formatDate(parsedInitialDate, 'dd/MMM');
    const finalDate = formatDate(parsedFinalDate, 'dd/MMM');

    const diffDays = differenceInDaysDate(parsedFinalDate, parsedInitialDate);

    return `${label.toUpperCase()} - (${initialDate.toUpperCase()} - ${finalDate.toUpperCase()}) - (${diffDays} ${i18next.t(
        'Day',
        { count: diffDays },
    )})`;
};

export const getChartOptionsEconomic = ({
    consumptionHistory,
    countryCurrencyIso,
    countryCurrencyLocale,
    onLegendClick,
    yearCategories,
}) => ({
    chart: {
        background: '#ffffff00',
        events: { legendClick: onLegendClick },
        stacked: true,
        toolbar: { show: false },
    },
    chartJsOptions: {
        plugins: {
            tooltip: {
                callbacks: {
                    label: (context) => {
                        return (
                            context.dataset.label +
                            ': ' +
                            numberFormat(context.parsed.y, {
                                currency: countryCurrencyIso,
                                locale: countryCurrencyLocale,
                                style: 'currency',
                            })
                        );
                    },
                    title: (context) => {
                        const ctx = context[0];
                        const values = ctx.parsed._stacks.y._visualValues;
                        const _metasets = ctx.chart._metasets;
                        const valuesLength = Object.keys(values).length - 1;
                        let total = 0;

                        for (let i = 0; i <= valuesLength; i++) {
                            if (_metasets[i].hidden) continue;
                            total += values[i];
                        }

                        const formattedTotal = numberFormat(total, {
                            currency: countryCurrencyIso,
                            locale: countryCurrencyLocale,
                            style: 'currency',
                        });

                        return `${getTooltipDateRange({
                            index: ctx.dataIndex,
                            seriesEnergy: consumptionHistory,
                        })} - ${formattedTotal}`;
                    },
                },
            },
        },
        scales: {
            x: { grid: { display: false }, stacked: true },
            y: {
                beginAtZero: true,
                stacked: true,
                ticks: {
                    callback: (val) =>
                        numberFormat(val, {
                            currency: countryCurrencyIso,
                            locale: countryCurrencyLocale,
                            style: 'currency',
                        }),
                },
            },
        },
    },
    colors: CHART_COLORS,
    consumptionHistory,
    dataLabels: { enabled: false },
    legend: { show: true, showForSingleSeries: true },
    tooltip: {
        x: {
            formatter: (value, { dataPointIndex, w }) =>
                getEconomicChartTooltipMessage({
                    consumptionHistory,
                    countryCurrencyIso,
                    countryCurrencyLocale,
                    dataPointIndex,
                    value,
                    w,
                }),
        },
        y: {
            formatter: (val) =>
                numberFormat(val, {
                    currency: countryCurrencyIso,
                    locale: countryCurrencyLocale,
                    style: 'currency',
                }),
        },
    },
    xaxis: { categories: yearCategories },
    yaxis: {
        decimalsInFloat: 0,
        labels: {
            formatter: (val) =>
                numberFormat(val, {
                    currency: countryCurrencyIso,
                    locale: countryCurrencyLocale,
                    style: 'currency',
                }),
        },
    },
});

// Labels
export const getArrayHours = () =>
    Array.from({ length: HOURS_IN_DAY }, (_, i) => String(i).padStart(2, '0'));

export const getChartMonthTypeLabel = (month, year) => {
    const labels = [];

    const initialDate = new Date(year, month - 1, 1);
    const finalDate = new Date(year, month, 0);
    for (
        let date = initialDate;
        date <= finalDate;
        date.setDate(date.getDate() + 1)
    ) {
        labels.push(formatDate(date, 'dd (EE)'));
    }

    return labels;
};

export const getDaysNames = () => [
    ...DAYS_NAMES.map((day) => getDayLabel(day)).slice(1),
    getDayLabel(DAYS_NAMES[0]),
];

export const getChartCategories = (type, month, year) => {
    switch (type) {
        case 0:
            return MONTHS.map((monthItem) => getMonthLabel(monthItem));
        case 1:
            return getChartMonthTypeLabel(month, year);
        case 2:
            return getDaysNames();
        case 3:
            return getArrayHours();
    }
};

// Series
export const getSeriesByOption = ({ data, options } = {}) =>
    options[data?.selectedOption](data);

const createFilledSeries = (series, length) => {
    const filledSeries = {};
    for (const { name, shouldPopulate = true } of series) {
        if (!shouldPopulate) continue;

        filledSeries[name] = createFilledArray(length);
    }

    return filledSeries;
};

const populateSeries = ({
    dataIndex,
    populatedSeriesData,
    multiplier = 1,
    serieIndex,
    series,
}) => {
    for (const { data, name, shouldPopulate = true } of series) {
        if (!shouldPopulate) continue;

        populatedSeriesData[name][serieIndex] +=
            data[dataIndex] * multiplier || 0;
    }
};

export const populateDailySeries = ({
    excludedSeriesFromMinMax,
    realIndex,
    returnMinAndMax,
    selectedDays,
    selectedPeriod,
    selectedWeeks,
    selectedYear,
    series,
} = {}) => {
    const populatedData = { series: createFilledSeries(series, HOURS_IN_DAY) };

    const daysInPeriod = getDaysByWeek(selectedWeeks);
    const initialIndex = selectedYear * HOURS_IN_YEAR;
    const multiplier = 1 / (selectedWeeks.length * selectedDays.length);

    for (const day of daysInPeriod) {
        const dayObject = realIndex?.[selectedPeriod]?.[day - 1];
        if (!dayObject) continue;

        const dayType = dayObject.day;
        if (!selectedDays.includes(dayType)) continue;

        for (const hour in dayObject) {
            if (hour === 'total' || hour === 'day') continue;

            populateSeries({
                dataIndex: initialIndex + dayObject[hour],
                populatedSeriesData: populatedData.series,
                multiplier,
                serieIndex: hour,
                series,
            });
        }
    }

    if (returnMinAndMax) {
        const allValues = Object.entries(populatedData.series)
            .flatMap(([key, value]) =>
                excludedSeriesFromMinMax?.includes(key) ? [] : value,
            )
            .sort((a, b) => a - b);

        populatedData.minValue = allValues[0];
        populatedData.maxValue = allValues[allValues.length - 1];
    }

    return populatedData;
};

export const populateWeeklySeries = ({
    realIndex,
    selectedPeriod,
    selectedWeeks,
    selectedYear,
    series,
} = {}) => {
    const populatedData = { series: createFilledSeries(series, DAYS_IN_WEEK) };

    const daysInPeriod = getDaysByWeek(selectedWeeks);
    const initialIndex = selectedYear * HOURS_IN_YEAR;
    const multiplier = 1 / selectedWeeks.length;

    for (const day of daysInPeriod) {
        const dayObject = realIndex?.[selectedPeriod]?.[day - 1];
        if (!dayObject) continue;

        const dayType = dayObject.day === 0 ? 6 : dayObject.day - 1;

        for (const hour in dayObject) {
            if (hour === 'total' || hour === 'day') continue;

            populateSeries({
                dataIndex: initialIndex + dayObject[hour],
                populatedSeriesData: populatedData.series,
                multiplier,
                serieIndex: dayType,
                series,
            });
        }
    }
    return populatedData;
};

export const populatePeriodicallySeries = ({
    realIndex,
    selectedPeriod,
    selectedYear,
    series,
} = {}) => {
    const totalDays = realIndex[selectedPeriod]?.length || 0;

    const populatedData = { series: createFilledSeries(series, totalDays) };

    const initialIndex = selectedYear * HOURS_IN_YEAR;

    for (let day = 0; day < totalDays; day++) {
        for (const hour in realIndex[selectedPeriod][day]) {
            if (hour === 'total' || hour === 'day') continue;

            populateSeries({
                dataIndex: initialIndex + realIndex[selectedPeriod][day][hour],
                populatedSeriesData: populatedData.series,
                serieIndex: day,
                series,
            });
        }
    }

    return populatedData;
};

export const hexToRgba = (hex, alpha = 1, object = false) => {
    if (!hex) return;
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if (object) return { r, g, b, a: alpha };
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const getGradient = ({
    color,
    context,
    opacityFrom = 0.1,
    opacityTo = 0.9,
    shadeIntensity = 1,
    stops = [0, 100, 100],
} = {}) => {
    if (!color) return;
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    // This case happens on initial chart load
    if (!chartArea) return;

    let gradient = ctx.createLinearGradient(
        0,
        chartArea.bottom,
        0,
        chartArea.top,
    );
    // Calcular los colores con el shadeIntensity y opacidades
    const { r, g, b } = hexToRgba(color, 1, true);
    const colorFrom = `rgba(${r * shadeIntensity}, ${g * shadeIntensity}, ${
        b * shadeIntensity
    }, ${opacityFrom})`; // Rojo con la intensidad y opacidad inicial
    const colorTo = `rgba(${r * shadeIntensity}, ${g * shadeIntensity}, ${
        b * shadeIntensity
    }, ${opacityTo})`; // Rojo con la intensidad y opacidad final

    // Añadir los stops al gradiente
    gradient.addColorStop(stops[0] / 100, colorFrom);
    gradient.addColorStop(stops[1] / 100, colorTo);
    gradient.addColorStop(stops[2] / 100, colorTo);
    return gradient;
};

const buildEnergyPoolChartProps = ({
    chartControlsConfig,
    countryCurrencyLocale,
    currentChartConfig,
    defaultVisibility,
    handleOnLegendClick,
}) => {
    const series = currentChartConfig.series.map((serie) => serie);
    const data = {
        datasets: series.map(
            ({ color, data, name: label, type, stack = undefined }) => {
                const extraConfig = {};
                const hidden =
                    (!isEmpty(defaultVisibility) &&
                        !defaultVisibility?.includes(label)) ||
                    defaultVisibility?.length === 0;
                return {
                    ...extraConfig,
                    backgroundColor: color,
                    borderColor: color,
                    data,
                    hidden,
                    label,
                    stack,
                    type,
                };
            },
        ),
        labels: currentChartConfig.options.xaxis?.categories || [],
    };

    const options = {
        type: 'bar',
        plugins: {
            ...(handleOnLegendClick && {
                legend: {
                    onClick: function (_, legendItem) {
                        const chart = this.chart;
                        const label = legendItem.text;
                        handleOnLegendClick({
                            label,
                            series: chart.data.datasets,
                        });
                    },
                },
            }),
            tooltip: {
                callbacks: {
                    label: (context) => {
                        return (
                            context.dataset.label +
                            ': ' +
                            numberFormat(context.parsed.y, {
                                decimals:
                                    chartControlsConfig.option === 0 ? 0 : 3,
                                locale: countryCurrencyLocale,
                                style: 'decimal',
                                unit: '',
                            })
                        );
                    },
                },
                mode: 'index',
                position: 'nearest',
            },
        },
        scales: {
            x: { grid: { display: false } },
            y: {
                stacked: true,
                ticks: {
                    callback: (val) =>
                        numberFormat(val, {
                            locale: countryCurrencyLocale,
                            style: 'decimal',
                            unit: 'kWh',
                        }),
                    count: null,
                },
            },
        },
    };

    return { data, key: 'chartBag', options, series };
};

export const buildChartJsProps = ({
    chartControlsConfig,
    chartId,
    countryCurrencyIso,
    countryCurrencyLocale,
    currentChartConfig,
    defaultVisibility,
    handleOnLegendClick,
    isSelfConsumption,
    selectedStacks,
} = {}) => {
    if (
        chartId === 'chartCurrentEconomic' &&
        selectedStacks === 'chartProposedEnergyBag'
    ) {
        return buildEnergyPoolChartProps({
            chartControlsConfig,
            countryCurrencyIso,
            countryCurrencyLocale,
            currentChartConfig,
            defaultVisibility: defaultVisibility[chartId],
            handleOnLegendClick,
        });
    }

    let labelsMap = [];
    let series = currentChartConfig?.series;
    let yAxisIDs = new Set(['y']);
    let extraOptionsScaleY = {};
    let extraOptionsScaleY1 = {};

    if (!series)
        return { series: [], data: { datasets: [], labels: [] }, options: {} };

    const colors =
        chartId === 'chartEnergy' && chartControlsConfig?.option === 0
            ? ENERGY_CHART_COLORS
            : CHART_COLORS;

    if (chartId !== 'chartEnergy') {
        series = series.map((serie, index) => {
            const label = labelsMap.find((label) => label.label === serie.name);
            const color = label?.color || colors[index % colors.length];
            if (!label) labelsMap.push({ label: serie.name, color });

            return { ...serie, color };
        });
    }

    const _defaultVisibility = defaultVisibility?.[chartId];

    let data = {
        datasets: series.map(
            (
                { color, data, id, name: label, type, stack = undefined },
                index,
            ) => {
                const _color = color ? color : colors[index % colors.length];
                const extraConfig = {};
                const hidden =
                    (!isEmpty(_defaultVisibility) &&
                        !_defaultVisibility?.includes(label)) ||
                    _defaultVisibility?.length === 0;
                let backgroundColor = hexToRgba(_color, 0.8);
                let fill = false;
                let _type = type;
                let yAxisID = 'y';

                if (type === 'column') _type = 'bar';
                if (type === 'area') _type = 'line';

                if (currentChartConfig.key !== 'energy-chart-NETMET')
                    fill = _type !== 'line';

                if (type === 'area') {
                    fill = true;
                    backgroundColor = function (context) {
                        return getGradient({ color, context });
                    };
                }

                if (label === 'SOC (%)') {
                    extraConfig.borderDash = [5, 5];
                    yAxisID = 'SOC (%)';
                    yAxisIDs.add(yAxisID);

                    if (currentChartConfig?.options?.yaxis?.[index]) {
                        extraOptionsScaleY1 = {
                            max: currentChartConfig?.options?.yaxis[index].max,
                            min: Math.ceil(
                                currentChartConfig?.options?.yaxis[index].min,
                            ),
                        };
                    }
                } else {
                    if (currentChartConfig?.options?.yaxis?.[index]) {
                        extraOptionsScaleY = {
                            max: currentChartConfig?.options?.yaxis[index].max,
                            min: currentChartConfig?.options?.yaxis[index].min,
                        };
                    }
                }

                return {
                    ...extraConfig,
                    backgroundColor,
                    borderColor: _color,
                    data,
                    fill,
                    hidden,
                    id,
                    label,
                    stack,
                    type: _type,
                    yAxisID,
                };
            },
        ),
        labels: currentChartConfig.options.xaxis?.categories || [],
    };

    let options = {
        type: 'line',
        interaction: { axis: 'x', intersect: false, mode: 'nearest' },
        plugins: {
            ...(handleOnLegendClick && {
                legend: {
                    onClick: function (_, legendItem) {
                        const chart = this.chart;
                        const label = legendItem.text;
                        handleOnLegendClick({
                            label,
                            series: chart.data.datasets,
                        });
                    },
                },
            }),
            tooltip: {
                callbacks: {
                    label: (context) => {
                        return (
                            context.dataset.label +
                            ': ' +
                            numberFormat(context.parsed.y, {
                                decimals:
                                    chartControlsConfig?.option === 0 ? 0 : 3,
                                locale: countryCurrencyLocale,
                                style: 'decimal',
                                unit: '',
                            })
                        );
                    },
                    ...(chartControlsConfig?.option === 0
                        ? {
                              title: (context) => {
                                  const ctx = context[0];
                                  return `${getTooltipDateRange({
                                      index: ctx.dataIndex,
                                      seriesEnergy: isSelfConsumption
                                          ? currentChartConfig.options
                                                .consumptionHistory[
                                                chartControlsConfig?.year || 0
                                            ]
                                          : currentChartConfig.options
                                                .consumptionHistory,
                                  })}`;
                              },
                          }
                        : {}),
                },
                mode: 'index',
                position: 'nearest',
            },
        },
        scales: {
            x: { grid: { display: false } },
            y: {
                ...extraOptionsScaleY,
                ticks: {
                    callback: (val) =>
                        numberFormat(val, {
                            decimals: chartControlsConfig?.option === 3 ? 3 : 0,
                            locale: countryCurrencyLocale,
                            style: 'decimal',
                        }),
                },
                title: {
                    display: true,
                    text: chartControlsConfig?.option === 3 ? 'kW' : 'kWh',
                },
            },
            'SOC (%)': {
                ...extraOptionsScaleY1,
                display: yAxisIDs.has('SOC (%)'),
                grid: { drawOnChartArea: false },
                position: 'right',
                ticks: {
                    callback: (val) =>
                        numberFormat(val, {
                            decimals: 0,
                            locale: countryCurrencyLocale,
                            style: 'decimal',
                            unit: '%',
                        }),
                },
            },
        },
    };

    const datasetsCopy = data.datasets;

    if (currentChartConfig.key === 'chartEconomic') {
        options = {
            ...options,
            type: 'bar',
            plugins: {
                legend: {
                    ...options.plugins.legend,
                    onHover: function (_, legendItem) {
                        const chart = this.chart;
                        const label = legendItem.text;
                        chart.data.datasets.forEach((dataset) => {
                            if (!dataset.backgroundColor) return;
                            if (dataset.label !== label) {
                                dataset.backgroundColor = 'rgba(0,0,0,0.05)';
                                dataset.borderColor = 'rgba(0,0,0,0)';
                            }
                        });
                        chart.update();
                    },
                    onLeave: function () {
                        const chart = this.chart;
                        chart.data.datasets.forEach((dataset, index) => {
                            if (!dataset.backgroundColor) return;
                            dataset.backgroundColor =
                                datasetsCopy[index].backgroundColor;
                            dataset.borderColor =
                                datasetsCopy[index].borderColor;
                        });
                        chart.update();
                    },
                    labels: {
                        generateLabels: (chart) => {
                            const {
                                labels: {
                                    color,
                                    useBorderRadius,
                                    borderRadius,
                                },
                            } = chart.legend.options;
                            const datasetLabels = chart.data.datasets.map(
                                (dataset) => dataset.label,
                            );
                            const uniqueLabels = [...new Set(datasetLabels)];
                            const labels = uniqueLabels.map((text) => {
                                const datasetIndex =
                                    datasetLabels.indexOf(text);
                                const dataset =
                                    chart.data.datasets[datasetIndex];
                                let hidden = false;
                                if (
                                    _defaultVisibility &&
                                    _defaultVisibility.length >= 0
                                ) {
                                    hidden =
                                        !_defaultVisibility?.includes(text);
                                }
                                return {
                                    borderRadius,
                                    boxHeight: 12,
                                    boxWidth: 12,
                                    fillStyle: dataset.backgroundColor,
                                    fontColor: color,
                                    hidden,
                                    strokeStyle: dataset.backgroundColor,
                                    text,
                                    useBorderRadius,
                                };
                            });
                            return labels;
                        },
                    },
                },
                tooltip: {
                    callbacks: {
                        beforeTitle: (context) => {
                            const ctx = context[0];
                            if (ctx.dataset.stack === 'current')
                                return i18next.t('Current');
                            if (ctx.dataset.stack === 'proposed')
                                return i18next.t('Proposed', {
                                    context: 'female',
                                });
                        },
                        label: (context) => {
                            if (selectedStacks === 'chartProposedEnergyBag') {
                                return (
                                    context.dataset.label +
                                    ': ' +
                                    numberFormat(context.parsed.y, {
                                        locale: countryCurrencyLocale,
                                        style: 'decimal',
                                        unit: 'kWh',
                                    })
                                );
                            }

                            return (
                                context.dataset.label +
                                ': ' +
                                numberFormat(context.parsed.y, {
                                    currency: countryCurrencyIso,
                                    locale: countryCurrencyLocale,
                                    style: 'currency',
                                })
                            );
                        },
                        title: (context) => {
                            const ctx = context[0];

                            if (selectedStacks === 'chartProposedEnergyBag')
                                return `${getTooltipDateRange({
                                    index: ctx.dataIndex,
                                    seriesEnergy:
                                        currentChartConfig.options
                                            .consumptionHistory,
                                })}`;

                            const values = ctx.parsed._stacks.y._visualValues;
                            const stack = ctx.dataset.stack;
                            const _metasets = ctx.chart._metasets.filter(
                                (set) => set.stack === stack,
                            );
                            const valuesLength = Object.keys(values).length - 1;
                            let total = 0;
                            for (let i = 0; i <= valuesLength; i++) {
                                const dataset = _metasets[i];
                                const datasetIndex = dataset.index;
                                const hidden =
                                    (!isEmpty(_defaultVisibility) &&
                                        !_defaultVisibility?.includes(
                                            dataset.label,
                                        )) ||
                                    _defaultVisibility?.length === 0;
                                if (hidden) continue;
                                total += values[datasetIndex];
                            }
                            const formattedTotal = numberFormat(total, {
                                currency: countryCurrencyIso,
                                locale: countryCurrencyLocale,
                                style: 'currency',
                            });

                            return `${getTooltipDateRange({
                                index: ctx.dataIndex,
                                seriesEnergy:
                                    currentChartConfig.options
                                        .consumptionHistory,
                            })} - ${formattedTotal}`;
                        },
                    },
                    mode: 'point',
                    position: 'nearest',
                },
            },
            scales: {
                x: { grid: { display: false } },
                y: {
                    stacked: selectedStacks !== 'chartProposedEnergyBag',
                    ticks: {
                        callback: (val) => {
                            if (selectedStacks === 'chartProposedEnergyBag') {
                                return numberFormat(val, {
                                    locale: countryCurrencyLocale,
                                    style: 'decimal',
                                    unit: 'kWh',
                                });
                            }

                            return numberFormat(val, {
                                currency: countryCurrencyIso,
                                locale: countryCurrencyLocale,
                                style: 'currency',
                            });
                        },
                        count: null,
                        maxTicksLimit: 10,
                    },
                },
            },
        };
    }

    return { data, key: currentChartConfig?.key, options, series };
};

export const exportChartToCSV = (data) => {
    const labels = data.labels;
    const datasets = data.datasets;

    let csvContent = 'data:text/csv;charset=utf-8,';

    csvContent +=
        'category,' + datasets.map((dataset) => dataset.label).join(',') + '\n';

    labels.forEach((label, index) => {
        const row = [label];
        datasets.forEach((dataset) => {
            row.push(dataset.data[index]);
        });
        csvContent += row.join(',') + '\n';
    });

    const encodedUri = encodeURI(csvContent);

    downloadFile(encodedUri, 'chart-data.csv');
};
